import {useEffect, useState} from 'react';
import {API_BASE} from '../../utils/api/reports';
import buildHeaders from '../../utils/api/fetchHeaders';


/**
 * Hook to fetch entities for a specific segment on-demand
 */
export function useSegmentEntities(segmentId) {
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!segmentId) {
            setEntities([]);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        const controller = new AbortController();

        (async () => {
            try {
                const response = await fetch(
                    `${API_BASE}/entities/segments/${segmentId}/mappings`,
                    {
                        method: 'GET',
                        headers: buildHeaders({
                            'Accept': 'application/json',
                        }),
                        signal: controller.signal,
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch entities');
                }

                const data = await response.json();
                
                // Map the backend response to our entity format
                const mappedEntities = (data?.mappings || []).map(mapping => ({
                    entityId: mapping.entity?.entityId,
                    wikidataId: mapping.entity?.wikidataId,
                    entityType: mapping.entity?.entityType,
                    canonicalName: mapping.entity?.canonicalName,
                    description: mapping.entity?.description,
                    aliases: mapping.entity?.aliases,
                    imageUrl: mapping.entity?.imageUrl,
                    mentionText: mapping.mentionText,
                    startOffset: mapping.startOffset,
                    endOffset: mapping.endOffset,
                    confidence: mapping.confidence,
                    nerScore: mapping.nerScore ?? mapping.confidence,
                    linkingScore: mapping.linkingScore,
                    wasDisambiguated: mapping.wasDisambiguated,
                    extractedByModel: mapping.extractedByModel,
                    discoveredByModel: mapping.entity?.discoveredByModel,
                }));

                setEntities(mappedEntities);
            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError(err);
                    setEntities([]);
                }
            } finally {
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [segmentId]);

    return {entities, loading, error};
}

/**
 * Hook to fetch entities for multiple segments in parallel
 * Returns a map of segmentId -> entities array
 */
export function useMultipleSegmentEntities(segments) {
    const [entitiesMap, setEntitiesMap] = useState(new Map());
    const [loadingMap, setLoadingMap] = useState(new Map());

    useEffect(() => {
        if (!segments || !Array.isArray(segments) || segments.length === 0) {
            setEntitiesMap(new Map());
            setLoadingMap(new Map());
            return;
        }

        const controllers = new Map();

        // Fetch entities for each segment in parallel
        segments.forEach((segment) => {
            const segmentId = segment?.segmentId;
            if (!segmentId) return;

            // Mark as loading
            setLoadingMap(prev => new Map(prev).set(segmentId, true));

            const controller = new AbortController();
            controllers.set(segmentId, controller);

            (async () => {
                try {
                    const response = await fetch(
                        `${API_BASE}/entities/segments/${segmentId}/mappings`,
                        {
                            method: 'GET',
                            headers: buildHeaders({
                                'Accept': 'application/json',
                            }),
                            signal: controller.signal,
                        }
                    );

                    if (!response.ok) {
                        throw new Error('Failed to fetch entities');
                    }

                    const data = await response.json();
                    
                    // Map the backend response to our entity format
                    const mappedEntities = (data?.mappings || []).map(mapping => ({
                        entityId: mapping.entity?.entityId,
                        wikidataId: mapping.entity?.wikidataId,
                        entityType: mapping.entity?.entityType,
                        canonicalName: mapping.entity?.canonicalName,
                        description: mapping.entity?.description,
                        aliases: mapping.entity?.aliases,
                        imageUrl: mapping.entity?.imageUrl,
                        mentionText: mapping.mentionText,
                        startOffset: mapping.startOffset,
                        endOffset: mapping.endOffset,
                        confidence: mapping.confidence,
                        nerScore: mapping.nerScore ?? mapping.confidence,
                        linkingScore: mapping.linkingScore,
                        wasDisambiguated: mapping.wasDisambiguated,
                        extractedByModel: mapping.extractedByModel,
                        discoveredByModel: mapping.entity?.discoveredByModel,
                    }));

                    // Update entities map
                    setEntitiesMap(prev => {
                        const newMap = new Map(prev);
                        newMap.set(segmentId, mappedEntities);
                        return newMap;
                    });
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        console.error(`Failed to fetch entities for segment ${segmentId}:`, err);
                        setEntitiesMap(prev => {
                            const newMap = new Map(prev);
                            newMap.set(segmentId, []);
                            return newMap;
                        });
                    }
                } finally {
                    setLoadingMap(prev => {
                        const newMap = new Map(prev);
                        newMap.set(segmentId, false);
                        return newMap;
                    });
                }
            })();
        });

        // Cleanup: abort all ongoing requests
        return () => {
            controllers.forEach(controller => controller.abort());
        };
    }, [segments]);

    return {entitiesMap, loadingMap};
}

